import { useAuthContext } from '@/auth/useAuthContext';
import {
  ApiError,
  type ImportDateFormat,
  type ImportFieldDef,
  type ImportHeaderMapping,
  type ImportJob,
  type ImportKind,
  type ImportMappingConfidence,
  type ImportPreset,
  type ImportRow,
  type ImportRowResult,
  type ImportSummary,
  listImportJobs,
  mapImportHeaders,
  runImport,
} from '@/lib/api';
import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  CalendarDays,
  Check,
  CheckCircle2,
  ClipboardPaste,
  FileSpreadsheet,
  History,
  PawPrint,
  RotateCcw,
  Syringe,
  Upload,
  Users,
} from 'lucide-react';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';

// ---------------------------------------------------------------------------
// Client-side CSV parsing (preview + mapping). The server re-validates; this is
// only to drive the column-mapping step and the sample-value column.
// ---------------------------------------------------------------------------

function parseCsv(input: string): string[][] {
  let text = input;
  if (text.charCodeAt(0) === 0xfeff) text = text.slice(1);
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;
  let i = 0;
  const n = text.length;
  let hasContent = false;
  const endField = () => {
    row.push(field);
    field = '';
  };
  const endRecord = () => {
    endField();
    rows.push(row);
    row = [];
    hasContent = false;
  };
  while (i < n) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i += 1;
        continue;
      }
      field += ch;
      i += 1;
      continue;
    }
    if (ch === '"') {
      inQuotes = true;
      hasContent = true;
      i += 1;
      continue;
    }
    if (ch === ',') {
      endField();
      hasContent = true;
      i += 1;
      continue;
    }
    if (ch === '\n') {
      endRecord();
      i += 1;
      continue;
    }
    if (ch === '\r') {
      endRecord();
      i += text[i + 1] === '\n' ? 2 : 1;
      continue;
    }
    field += ch;
    hasContent = true;
    i += 1;
  }
  if (hasContent || field.length > 0 || row.length > 0) endRecord();
  return rows.filter((r) => r.some((cell) => cell.trim().length > 0));
}

// ---------------------------------------------------------------------------
// Static catalogue (labels only — the server owns the synonym tables)
// ---------------------------------------------------------------------------

const UNMAPPED = '__unmapped__';
const PREVIEW_ROWS = 20;

const PRESETS: Array<{ id: ImportPreset; label: string; description: string }> = [
  {
    id: 'kennelbooker',
    label: 'KennelBooker',
    description: 'Customer, pet, vaccination and booking exports.',
  },
  { id: 'gingr', label: 'Gingr', description: 'Owner / animal / reservation exports.' },
  { id: 'petexec', label: 'PetExec', description: 'Owner / pet / booking exports.' },
  {
    id: 'generic',
    label: 'Other spreadsheet',
    description: 'Any CSV with a header row — common column names are matched.',
  },
];

const KINDS: Array<{ id: ImportKind; label: string; noun: string; Icon: typeof Users }> = [
  { id: 'customers', label: 'Customers', noun: 'customer', Icon: Users },
  { id: 'pets', label: 'Pets', noun: 'pet', Icon: PawPrint },
  { id: 'vaccinations', label: 'Vaccinations', noun: 'vaccination', Icon: Syringe },
  { id: 'bookings', label: 'Bookings', noun: 'booking', Icon: CalendarDays },
];

const DATE_FORMATS: Array<{ id: ImportDateFormat; label: string; example: string }> = [
  { id: 'dmy', label: 'Day / month / year', example: '05/01/2026 = 5 January (UK, IE, AU, NZ)' },
  { id: 'mdy', label: 'Month / day / year', example: '05/01/2026 = 1 May (US)' },
  { id: 'iso', label: 'ISO year-month-day', example: '2026-01-05' },
];

const STEPS = ['Source', 'Upload', 'Review mapping', 'Preview & import'] as const;

function kindMeta(kind: ImportKind): (typeof KINDS)[number] {
  return (
    KINDS.find((k) => k.id === kind) ?? {
      id: 'customers',
      label: 'Customers',
      noun: 'customer',
      Icon: Users,
    }
  );
}

function plural(n: number, noun: string) {
  return `${n} ${noun}${n === 1 ? '' : 's'}`;
}

function formatJobDate(iso: string) {
  try {
    return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' }).format(
      new Date(iso),
    );
  } catch {
    return iso.slice(0, 10);
  }
}

const CONFIDENCE_STYLE: Record<ImportMappingConfidence, { label: string; className: string }> = {
  high: { label: 'High', className: 'bg-success/10 text-success ring-hairline' },
  medium: { label: 'Medium', className: 'bg-warning/15 text-warning-foreground ring-hairline' },
  low: { label: 'Low — check', className: 'bg-secondary text-muted-foreground ring-hairline' },
  none: { label: 'Unmapped', className: 'bg-destructive/10 text-destructive ring-hairline' },
};

const ROW_STATUS_STYLE: Record<ImportRowResult['status'], { label: string; className: string }> = {
  created: { label: 'Will import', className: 'bg-success/10 text-success ring-hairline' },
  skipped: {
    label: 'Skip (duplicate)',
    className: 'bg-secondary text-muted-foreground ring-hairline',
  },
  error: { label: 'Error', className: 'bg-destructive/10 text-destructive ring-hairline' },
};

function describeError(err: unknown): string {
  if (err instanceof ApiError) {
    const issues = (err.body as { issues?: unknown } | null)?.issues;
    if (Array.isArray(issues) && issues.length > 0) return issues.join('; ');
    if (err.status === 403) return "You don't have permission to import this data.";
    if (err.code === 'facility_required') return 'Choose a facility for these bookings first.';
    return `The import could not be completed (${err.code}). Please try again.`;
  }
  return 'Something went wrong. Please try again.';
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

type Step = 1 | 2 | 3 | 4;

export function DataImport() {
  const { state } = useAuthContext();
  const facilities = state.me?.facilitiesInScope ?? [];
  const currency = state.me?.activeAccount?.currency ?? 'GBP';

  const [step, setStep] = useState<Step>(1);
  const [kind, setKind] = useState<ImportKind>('customers');
  const [preset, setPreset] = useState<ImportPreset>('kennelbooker');
  const [dateFormat, setDateFormat] = useState<ImportDateFormat>('dmy');
  const [facilityId, setFacilityId] = useState<string>('');

  const [fileName, setFileName] = useState<string | null>(null);
  const [pasted, setPasted] = useState('');
  const [headers, setHeaders] = useState<string[]>([]);
  const [dataRows, setDataRows] = useState<string[][]>([]);
  const [parseError, setParseError] = useState<string | null>(null);

  const [mapping, setMapping] = useState<ImportHeaderMapping | null>(null);
  const [fieldByColumn, setFieldByColumn] = useState<Record<number, string>>({});

  const [dryRun, setDryRun] = useState<ImportSummary | null>(null);
  const [finalResult, setFinalResult] = useState<ImportSummary | null>(null);
  const [busy, setBusy] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [jobs, setJobs] = useState<ImportJob[] | null>(null);

  const meta = kindMeta(kind);
  const fields: ImportFieldDef[] = mapping?.fields ?? [];

  const loadJobs = useCallback(async () => {
    try {
      const res = await listImportJobs();
      setJobs(res.jobs);
    } catch {
      setJobs([]);
    }
  }, []);

  useEffect(() => {
    void loadJobs();
  }, [loadJobs]);

  useEffect(() => {
    if (!facilityId && facilities.length === 1) setFacilityId(facilities[0]?.id ?? '');
  }, [facilities, facilityId]);

  const resetFile = useCallback(() => {
    setFileName(null);
    setPasted('');
    setHeaders([]);
    setDataRows([]);
    setParseError(null);
    setMapping(null);
    setFieldByColumn({});
    setDryRun(null);
    setFinalResult(null);
    setSubmitError(null);
  }, []);

  const startOver = useCallback(() => {
    resetFile();
    setStep(1);
  }, [resetFile]);

  // --- step 2: parse + auto-map --------------------------------------------

  async function ingest(text: string, name: string | null) {
    setParseError(null);
    const matrix = parseCsv(text);
    if (matrix.length === 0) {
      setParseError('That file appears to be empty.');
      return;
    }
    const hdr = matrix[0] ?? [];
    const rest = matrix.slice(1);
    if (rest.length === 0) {
      setParseError('No data rows found below the header.');
      return;
    }
    setBusy(true);
    try {
      const result = await mapImportHeaders({ headers: hdr, kind, preset });
      const initial: Record<number, string> = {};
      for (const col of result.columns) initial[col.index] = col.field ?? UNMAPPED;
      setFileName(name);
      setHeaders(hdr);
      setDataRows(rest);
      setMapping(result);
      setFieldByColumn(initial);
      setDryRun(null);
      setStep(3);
    } catch (err) {
      setParseError(describeError(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleFile(file: File) {
    try {
      const text = await file.text();
      await ingest(text, file.name);
    } catch {
      setParseError('Could not read that file. Make sure it is a .csv export.');
    }
  }

  // --- step 3: mapping review ----------------------------------------------

  const mappedFields = useMemo(() => new Set(Object.values(fieldByColumn)), [fieldByColumn]);
  const missingRequired = useMemo(() => {
    return fields
      .filter((f) => f.requiredIn?.includes(kind))
      .filter((f) => !mappedFields.has(f.key))
      .filter((f) => !(f.key === 'first_name' && mappedFields.has('name')));
  }, [fields, kind, mappedFields]);
  const unmappedColumns = useMemo(
    () =>
      headers
        .map((h, idx) => ({ h, idx }))
        .filter(({ idx }) => (fieldByColumn[idx] ?? UNMAPPED) === UNMAPPED),
    [headers, fieldByColumn],
  );
  const duplicateFields = useMemo(() => {
    const seen = new Map<string, number>();
    const dupes = new Set<string>();
    for (const v of Object.values(fieldByColumn)) {
      if (v === UNMAPPED) continue;
      seen.set(v, (seen.get(v) ?? 0) + 1);
      if ((seen.get(v) ?? 0) > 1) dupes.add(v);
    }
    return dupes;
  }, [fieldByColumn]);

  function sampleValues(idx: number): string[] {
    const out: string[] = [];
    for (const cells of dataRows) {
      const v = (cells[idx] ?? '').trim();
      if (v.length > 0 && !out.includes(v)) out.push(v);
      if (out.length === 3) break;
    }
    return out;
  }

  function buildRows(): ImportRow[] {
    return dataRows.map((cells) => {
      const obj: ImportRow = {};
      headers.forEach((_h, idx) => {
        const field = fieldByColumn[idx];
        if (!field || field === UNMAPPED) return;
        const val = cells[idx];
        if (typeof val === 'string') obj[field] = val;
      });
      return obj;
    });
  }

  function runOptions(isDryRun: boolean) {
    return {
      dryRun: isDryRun,
      dateFormat,
      preset,
      ...(kind === 'bookings' && facilityId ? { facilityId } : {}),
    };
  }

  // --- step 4: dry run + import --------------------------------------------

  async function handleDryRun() {
    setBusy(true);
    setSubmitError(null);
    try {
      const result = await runImport(kind, buildRows(), runOptions(true));
      setDryRun(result);
      setStep(4);
    } catch (err) {
      setSubmitError(describeError(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleImport() {
    setBusy(true);
    setSubmitError(null);
    try {
      const result = await runImport(kind, buildRows(), runOptions(false));
      setFinalResult(result);
      void loadJobs();
    } catch (err) {
      setSubmitError(describeError(err));
    } finally {
      setBusy(false);
    }
  }

  const canContinueFromSource = kind !== 'bookings' || facilities.length <= 1 || facilityId !== '';
  const lastJob = jobs?.[0] ?? null;

  return (
    <div className="space-y-6">
      <header>
        <p className="text-[11px] font-medium uppercase tracking-[0.2em] text-muted-foreground">
          Settings
        </p>
        <h1 className="font-heading text-2xl font-medium tracking-tight">Import data</h1>
        <p className="mt-2 max-w-2xl text-sm leading-relaxed text-muted-foreground">
          Switching from KennelBooker or another system? Import your book in an afternoon:
          customers, then pets, then vaccinations, then bookings. Every import previews first and is
          safe to re-run — duplicates are skipped, never doubled.
        </p>
      </header>

      {/* Stepper */}
      <ol className="flex flex-wrap items-center gap-2 text-xs" aria-label="Import steps">
        {STEPS.map((label, i) => {
          const n = (i + 1) as Step;
          const active = step === n;
          const done = step > n;
          return (
            <Fragment key={label}>
              <li
                className={[
                  'flex items-center gap-1.5 rounded-full px-3 py-1 ring-hairline',
                  active
                    ? 'bg-primary/10 text-primary font-medium'
                    : done
                      ? 'bg-secondary text-foreground'
                      : 'bg-secondary text-muted-foreground',
                ].join(' ')}
                aria-current={active ? 'step' : undefined}
              >
                {done ? (
                  <Check className="size-3" strokeWidth={3} />
                ) : (
                  <span className="num">{n}</span>
                )}
                {label}
              </li>
              {i < STEPS.length - 1 && (
                <ArrowRight className="size-3 text-muted-foreground" strokeWidth={1.75} />
              )}
            </Fragment>
          );
        })}
      </ol>

      {/* Step 1 — source */}
      {step === 1 && (
        <section className="space-y-6 rounded-2xl bg-surface p-6 ring-1 ring-border elevate">
          <div>
            <h2 className="font-heading text-base font-medium">Where is this data coming from?</h2>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Picking the right source improves the automatic column matching. You can still adjust
              every column before anything is imported.
            </p>
            <div className="mt-4 grid gap-2 sm:grid-cols-2">
              {PRESETS.map((p) => {
                const selected = preset === p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setPreset(p.id)}
                    aria-pressed={selected}
                    className={[
                      'flex items-start gap-3 rounded-xl px-4 py-3 text-left ring-1 press',
                      selected
                        ? 'bg-primary/10 ring-primary/40'
                        : 'bg-surface ring-border hover:bg-secondary',
                    ].join(' ')}
                  >
                    <span
                      className={[
                        'mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full ring-1',
                        selected
                          ? 'bg-primary text-primary-foreground ring-primary'
                          : 'ring-border-strong',
                      ].join(' ')}
                    >
                      {selected && <Check className="size-3" strokeWidth={3} />}
                    </span>
                    <span>
                      <span className="block text-sm font-medium text-foreground">{p.label}</span>
                      <span className="block text-xs text-muted-foreground">{p.description}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <h2 className="font-heading text-base font-medium">What are you importing?</h2>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Import in order — each step links to the one before it.
            </p>
            <div className="mt-3 inline-flex flex-wrap rounded-lg bg-secondary p-1 ring-1 ring-border">
              {KINDS.map(({ id, label, Icon }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setKind(id)}
                  aria-pressed={kind === id}
                  className={[
                    'flex items-center gap-1.5 rounded-md px-4 py-1.5 text-sm font-medium press',
                    kind === id
                      ? 'bg-surface text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground',
                  ].join(' ')}
                >
                  <Icon className="size-4" strokeWidth={1.75} />
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                Date format in the file
              </span>
              <select
                value={dateFormat}
                onChange={(e) => setDateFormat(e.target.value as ImportDateFormat)}
                className="mt-1 w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
              >
                {DATE_FORMATS.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.label} — {d.example}
                  </option>
                ))}
              </select>
              <span className="mt-1 block text-xs text-muted-foreground">
                US exports flip month and day. Amounts are read in {currency}.
              </span>
            </label>

            {kind === 'bookings' && facilities.length > 1 && (
              <label className="block">
                <span className="text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                  Facility for these bookings
                </span>
                <select
                  value={facilityId}
                  onChange={(e) => setFacilityId(e.target.value)}
                  className="mt-1 w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                >
                  <option value="">Choose a facility…</option>
                  {facilities.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.name}
                    </option>
                  ))}
                </select>
                <span className="mt-1 block text-xs text-muted-foreground">
                  Kennel / run labels are matched against this facility's runs.
                </span>
              </label>
            )}
          </div>

          <div className="flex justify-end">
            <button
              type="button"
              disabled={!canContinueFromSource}
              onClick={() => setStep(2)}
              className="flex items-center gap-1.5 rounded-lg bg-primary px-5 py-2 text-sm font-medium text-primary-foreground press disabled:opacity-50"
            >
              Continue
              <ArrowRight className="size-4" strokeWidth={1.75} />
            </button>
          </div>
        </section>
      )}

      {/* Step 2 — upload */}
      {step === 2 && (
        <section className="rounded-2xl bg-surface p-6 ring-1 ring-border elevate">
          <div className="grid gap-6 md:grid-cols-2 md:divide-x md:divide-border">
            <div className="text-center">
              <FileSpreadsheet
                className="mx-auto mb-3 size-7 text-muted-foreground"
                strokeWidth={1.5}
              />
              <p className="text-sm font-medium text-foreground">
                Upload the {meta.label.toLowerCase()} CSV from{' '}
                {PRESETS.find((p) => p.id === preset)?.label}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                {kind === 'customers' && 'Name, email, phone, address — columns are matched next.'}
                {kind === 'pets' &&
                  'Pet name plus the owner email (or name) so each pet links to a customer.'}
                {kind === 'vaccinations' &&
                  'Owner + pet plus either a vaccine name/expiry pair or one expiry column per vaccine.'}
                {kind === 'bookings' &&
                  'Owner + pet, arrival and departure dates, kennel and total. Past stays import as history.'}
              </p>
              <label className="mt-5 inline-flex cursor-pointer items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground press">
                <Upload className="size-4" strokeWidth={1.75} />
                Choose CSV
                <input
                  type="file"
                  accept=".csv,text/csv"
                  className="hidden"
                  data-testid="import-file"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) void handleFile(f);
                  }}
                />
              </label>
            </div>
            <div className="md:pl-6">
              <label className="block">
                <span className="flex items-center gap-1.5 text-sm font-medium text-foreground">
                  <ClipboardPaste className="size-4 text-muted-foreground" strokeWidth={1.75} />
                  Or paste CSV text
                </span>
                <textarea
                  value={pasted}
                  onChange={(e) => setPasted(e.target.value)}
                  rows={6}
                  placeholder={'Owner Email,Pet,Breed\nalice@example.com,Rex,Labrador'}
                  aria-label="Paste CSV"
                  className="mt-2 w-full rounded-md border border-border bg-surface px-3 py-2 font-mono text-xs focus:outline-none focus:ring-2 focus:ring-primary/40"
                />
              </label>
              <button
                type="button"
                disabled={busy || pasted.trim().length === 0}
                onClick={() => void ingest(pasted, null)}
                className="mt-2 flex items-center gap-1.5 rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-secondary press disabled:opacity-50"
              >
                Use pasted text
                <ArrowRight className="size-4" strokeWidth={1.75} />
              </button>
            </div>
          </div>
          {parseError !== null && (
            <p className="mt-4 flex items-center gap-1.5 text-sm text-destructive">
              <AlertCircle className="size-4" strokeWidth={1.75} />
              {parseError}
            </p>
          )}
          <div className="mt-6 flex justify-between">
            <button
              type="button"
              onClick={() => setStep(1)}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground press"
            >
              <ArrowLeft className="size-3.5" strokeWidth={1.75} />
              Back
            </button>
          </div>
        </section>
      )}

      {/* Step 3 — review mapping */}
      {step === 3 && mapping && (
        <div className="space-y-5">
          <section className="overflow-hidden rounded-2xl bg-surface ring-1 ring-border elevate">
            <div className="flex items-center justify-between gap-3 border-b border-border px-6 py-4">
              <div>
                <h2 className="font-heading text-base font-medium">Review column mapping</h2>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {fileName ?? 'Pasted CSV'} · <span className="num">{dataRows.length}</span> row
                  {dataRows.length === 1 ? '' : 's'} · <span className="num">{headers.length}</span>{' '}
                  columns · matched for {PRESETS.find((p) => p.id === preset)?.label}
                </p>
              </div>
              <button
                type="button"
                onClick={startOver}
                className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground press"
              >
                <RotateCcw className="size-3.5" strokeWidth={1.75} />
                Start over
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-[10.5px] uppercase tracking-[0.14em] text-muted-foreground">
                    <th className="px-6 py-2 font-medium">Column in file</th>
                    <th className="px-4 py-2 font-medium">Sample values</th>
                    <th className="px-4 py-2 font-medium">Imports as</th>
                    <th className="px-6 py-2 font-medium">Match</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {mapping.columns.map((col) => {
                    const current = fieldByColumn[col.index] ?? UNMAPPED;
                    const userChanged = current !== (col.field ?? UNMAPPED);
                    const confidence: ImportMappingConfidence = userChanged
                      ? current === UNMAPPED
                        ? 'none'
                        : 'high'
                      : col.confidence;
                    const chip = CONFIDENCE_STYLE[confidence];
                    const isDupe = current !== UNMAPPED && duplicateFields.has(current);
                    return (
                      <tr key={`${col.header}-${col.index}`}>
                        <td className="px-6 py-3 align-top">
                          <span className="font-medium text-foreground">
                            {col.header || (
                              <span className="text-muted-foreground">(blank header)</span>
                            )}
                          </span>
                        </td>
                        <td className="px-4 py-3 align-top text-xs text-muted-foreground">
                          {sampleValues(col.index).map((v) => (
                            <span
                              key={v}
                              className="mr-1.5 inline-block truncate rounded bg-secondary px-1.5 py-0.5 num"
                            >
                              {v.length > 28 ? `${v.slice(0, 28)}…` : v}
                            </span>
                          ))}
                        </td>
                        <td className="px-4 py-3 align-top">
                          <select
                            aria-label={`Map column ${col.header || col.index + 1}`}
                            value={current}
                            onChange={(e) =>
                              setFieldByColumn((prev) => ({ ...prev, [col.index]: e.target.value }))
                            }
                            className="w-full min-w-48 rounded-md border border-border bg-surface px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                          >
                            <option value={UNMAPPED}>— Ignore this column —</option>
                            {fields.map((f) => (
                              <option key={f.key} value={f.key}>
                                {f.label}
                                {f.requiredIn?.includes(kind) ? ' (required)' : ''}
                              </option>
                            ))}
                          </select>
                          {isDupe && (
                            <p className="mt-1 text-xs text-destructive">
                              Two columns map to the same field — the last one wins.
                            </p>
                          )}
                        </td>
                        <td className="px-6 py-3 align-top">
                          <span
                            className={[
                              'inline-block rounded-full px-2 py-0.5 text-xs font-medium',
                              chip.className,
                            ].join(' ')}
                          >
                            {userChanged ? 'Set by you' : chip.label}
                          </span>
                          {!userChanged && col.warning && (
                            <p className="mt-1 max-w-56 text-xs text-muted-foreground">
                              {col.warning}
                            </p>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>

          {(unmappedColumns.length > 0 || missingRequired.length > 0) && (
            <section className="space-y-2 rounded-xl bg-warning/10 px-5 py-4 ring-hairline">
              {missingRequired.length > 0 && (
                <p className="flex items-start gap-2 text-sm text-warning-foreground">
                  <AlertTriangle className="mt-0.5 size-4 shrink-0" strokeWidth={1.75} />
                  <span>
                    Map the required field{missingRequired.length === 1 ? '' : 's'}{' '}
                    <strong>{missingRequired.map((f) => f.label).join(', ')}</strong> before
                    continuing.
                  </span>
                </p>
              )}
              {unmappedColumns.length > 0 && (
                <p className="flex items-start gap-2 text-sm text-warning-foreground">
                  <AlertTriangle className="mt-0.5 size-4 shrink-0" strokeWidth={1.75} />
                  <span>
                    <span className="num">{unmappedColumns.length}</span> column
                    {unmappedColumns.length === 1 ? '' : 's'} will be ignored:{' '}
                    {unmappedColumns.map(({ h, idx }) => (
                      <span
                        key={`${h}-${idx}`}
                        className="mr-1 inline-block rounded bg-surface px-1.5 py-0.5 text-xs ring-hairline"
                      >
                        {h || '(blank)'}
                      </span>
                    ))}
                  </span>
                </p>
              )}
            </section>
          )}

          {submitError !== null && (
            <p className="flex items-center gap-1.5 text-sm text-destructive">
              <AlertCircle className="size-4" strokeWidth={1.75} />
              {submitError}
            </p>
          )}

          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => {
                resetFile();
                setStep(2);
              }}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground press"
            >
              <ArrowLeft className="size-3.5" strokeWidth={1.75} />
              Choose a different file
            </button>
            <button
              type="button"
              disabled={busy || missingRequired.length > 0}
              onClick={() => void handleDryRun()}
              className="flex items-center gap-1.5 rounded-lg bg-primary px-5 py-2 text-sm font-medium text-primary-foreground press disabled:opacity-50"
            >
              {busy ? 'Checking…' : 'Preview import'}
              <ArrowRight className="size-4" strokeWidth={1.75} />
            </button>
          </div>
        </div>
      )}

      {/* Step 4 — dry-run preview → import → done */}
      {step === 4 && dryRun && finalResult === null && (
        <div className="space-y-5">
          <section className="rounded-2xl bg-surface p-6 ring-1 ring-border elevate">
            <h2 className="font-heading text-base font-medium">
              Preview — nothing has been imported yet
            </h2>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Every row was checked against your account exactly as the real import will run it.
            </p>
            <dl className="mt-4 grid grid-cols-1 divide-y divide-border rounded-lg ring-1 ring-border sm:grid-cols-3 sm:divide-x sm:divide-y-0">
              <div className="px-4 py-3 text-center">
                <dd className="num text-2xl font-medium text-success">{dryRun.created}</dd>
                <dt className="text-[10.5px] uppercase tracking-[0.14em] text-muted-foreground">
                  Will import
                </dt>
              </div>
              <div className="px-4 py-3 text-center">
                <dd className="num text-2xl font-medium text-foreground">{dryRun.skipped}</dd>
                <dt className="text-[10.5px] uppercase tracking-[0.14em] text-muted-foreground">
                  Already exist
                </dt>
              </div>
              <div className="px-4 py-3 text-center">
                <dd
                  className={[
                    'num text-2xl font-medium',
                    dryRun.errors.length > 0 ? 'text-destructive' : 'text-foreground',
                  ].join(' ')}
                >
                  {dryRun.errors.length}
                </dd>
                <dt className="text-[10.5px] uppercase tracking-[0.14em] text-muted-foreground">
                  Errors
                </dt>
              </div>
            </dl>
            {(dryRun.warnings ?? []).map((w) => (
              <p key={w} className="mt-3 flex items-start gap-2 text-sm text-warning-foreground">
                <AlertTriangle className="mt-0.5 size-4 shrink-0" strokeWidth={1.75} />
                {w}
              </p>
            ))}
          </section>

          <section className="overflow-hidden rounded-2xl bg-surface ring-1 ring-border elevate">
            <div className="border-b border-border px-6 py-3">
              <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                First{' '}
                <span className="num">{Math.min(PREVIEW_ROWS, dryRun.rows?.length ?? 0)}</span> of{' '}
                <span className="num">{dryRun.rowCount ?? dataRows.length}</span> rows
              </p>
            </div>
            <ul className="divide-y divide-border">
              {(dryRun.rows ?? []).slice(0, PREVIEW_ROWS).map((r) => {
                const style = ROW_STATUS_STYLE[r.status];
                return (
                  <li key={r.row} className="flex items-start gap-4 px-6 py-3 text-sm">
                    <span className="num w-14 shrink-0 text-xs text-muted-foreground">
                      Row {r.row}
                    </span>
                    <span
                      className={[
                        'shrink-0 rounded-full px-2 py-0.5 text-xs font-medium',
                        style.className,
                      ].join(' ')}
                    >
                      {style.label}
                    </span>
                    <span className="min-w-0 flex-1">
                      {r.summary && (
                        <span className="block truncate text-foreground">{r.summary}</span>
                      )}
                      {r.reason && (
                        <span className="block text-xs text-muted-foreground">{r.reason}</span>
                      )}
                      {r.warnings?.map((w) => (
                        <span key={w} className="block text-xs text-warning-foreground">
                          {w}
                        </span>
                      ))}
                    </span>
                  </li>
                );
              })}
            </ul>
          </section>

          {dryRun.errors.length > 0 && (
            <ErrorList errors={dryRun.errors} title="Rows that will not be imported" />
          )}

          {submitError !== null && (
            <p className="flex items-center gap-1.5 text-sm text-destructive">
              <AlertCircle className="size-4" strokeWidth={1.75} />
              {submitError}
            </p>
          )}

          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => setStep(3)}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground press"
            >
              <ArrowLeft className="size-3.5" strokeWidth={1.75} />
              Adjust mapping
            </button>
            <button
              type="button"
              disabled={busy || dryRun.created === 0}
              onClick={() => void handleImport()}
              className="flex items-center gap-1.5 rounded-lg bg-primary px-5 py-2 text-sm font-medium text-primary-foreground press disabled:opacity-50"
            >
              <Upload className="size-4" strokeWidth={1.75} />
              {busy ? 'Importing…' : `Import ${plural(dryRun.created, meta.noun)}`}
            </button>
          </div>
        </div>
      )}

      {step === 4 && finalResult !== null && (
        <section className="space-y-5 rounded-2xl bg-surface p-6 ring-1 ring-border elevate">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="size-5 text-success" strokeWidth={1.75} />
            <h2 className="font-heading text-base font-medium">Import complete</h2>
          </div>
          <dl className="grid grid-cols-1 divide-y divide-border rounded-lg ring-1 ring-border sm:grid-cols-3 sm:divide-x sm:divide-y-0">
            <div className="px-4 py-3 text-center">
              <dd className="num text-2xl font-medium text-success">{finalResult.created}</dd>
              <dt className="text-[10.5px] uppercase tracking-[0.14em] text-muted-foreground">
                Imported
              </dt>
            </div>
            <div className="px-4 py-3 text-center">
              <dd className="num text-2xl font-medium text-foreground">{finalResult.skipped}</dd>
              <dt className="text-[10.5px] uppercase tracking-[0.14em] text-muted-foreground">
                Skipped (duplicate)
              </dt>
            </div>
            <div className="px-4 py-3 text-center">
              <dd
                className={[
                  'num text-2xl font-medium',
                  finalResult.errors.length > 0 ? 'text-destructive' : 'text-foreground',
                ].join(' ')}
              >
                {finalResult.errors.length}
              </dd>
              <dt className="text-[10.5px] uppercase tracking-[0.14em] text-muted-foreground">
                Errors
              </dt>
            </div>
          </dl>
          {finalResult.errors.length > 0 && (
            <ErrorList errors={finalResult.errors} title="Rows that could not be imported" />
          )}
          <div className="flex justify-end">
            <button
              type="button"
              onClick={startOver}
              className="flex items-center gap-1.5 rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-secondary press"
            >
              <RotateCcw className="size-4" strokeWidth={1.75} />
              Import another file
            </button>
          </div>
        </section>
      )}

      {/* Last job summary */}
      <section className="rounded-2xl bg-surface ring-1 ring-border">
        <div className="flex items-center gap-2 border-b border-border px-6 py-3">
          <History className="size-4 text-muted-foreground" strokeWidth={1.75} />
          <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
            Recent imports
          </p>
        </div>
        {jobs === null ? (
          <p className="px-6 py-4 text-sm text-muted-foreground">Loading…</p>
        ) : jobs.length === 0 ? (
          <p className="px-6 py-4 text-sm text-muted-foreground">No imports yet.</p>
        ) : (
          <ul className="divide-y divide-border">
            {jobs.slice(0, 5).map((job) => (
              <li
                key={job.id}
                className="flex flex-wrap items-center gap-x-4 gap-y-1 px-6 py-3 text-sm"
              >
                <span className="text-foreground">
                  Imported <span className="num font-medium">{job.created}</span> {job.kind} on{' '}
                  <span className="num">{formatJobDate(job.createdAt)}</span>
                  {' — '}
                  <span className={['num', job.errorCount > 0 ? 'text-destructive' : ''].join(' ')}>
                    {plural(job.errorCount, 'error')}
                  </span>
                  {job.skipped > 0 && (
                    <span className="text-muted-foreground">
                      , <span className="num">{job.skipped}</span> skipped
                    </span>
                  )}
                </span>
                {job.preset && (
                  <span className="rounded-full bg-secondary px-2 py-0.5 text-xs text-muted-foreground ring-hairline">
                    {PRESETS.find((p) => p.id === job.preset)?.label ?? job.preset}
                  </span>
                )}
                {job === lastJob && job.errors.length > 0 && (
                  <span className="w-full text-xs text-muted-foreground">
                    {job.errors
                      .slice(0, 3)
                      .map((e) => `Row ${e.row}: ${e.reason}`)
                      .join(' · ')}
                    {job.errors.length > 3 ? ' · …' : ''}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function ErrorList({ errors, title }: { errors: ImportSummary['errors']; title: string }) {
  return (
    <div className="overflow-hidden rounded-lg ring-1 ring-border">
      <div className="border-b border-border bg-secondary px-4 py-2">
        <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
          {title}
        </p>
      </div>
      <ul className="max-h-72 divide-y divide-border overflow-y-auto">
        {errors.map((e) => (
          <li key={`${e.row}-${e.reason}`} className="flex items-start gap-3 px-4 py-2 text-sm">
            <span className="num shrink-0 rounded bg-secondary px-1.5 py-0.5 text-xs text-muted-foreground">
              Row {e.row}
            </span>
            <span className="text-foreground">{e.reason}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
